import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { Card, CardBody, CardHeader, Button, Badge, Alert, Input, Textarea } from '../../components/ui';
import { UserForm } from '../../components/users';
import HtmlEmailEditor from '../../components/email/HtmlEmailEditor';
import CloseIOConfigTab from './CloseIOConfigTab';
import AttioConfigTab from './AttioConfigTab';
import MeetingsIntegrationsTab from './MeetingsIntegrationsTab';
import ProposalBrandingTab from '../../components/organizations/ProposalBrandingTab';
import { organizationAPI, userAPI } from '../../api';
import { useToast } from '../../contexts/ToastContext';
import { useOrg } from '../../hooks';
import { parseDomains, domainsToText } from '../../utils/domains';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'bd_team', label: 'BD Team' },
  { value: 'compliance_officer', label: 'Compliance Officer' },
  { value: 'viewer', label: 'Viewer' },
];

const OrganizationDetailPage = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { switchOrg, isSuperAdmin } = useOrg();

  const tabKeys = isSuperAdmin
    ? ['members', 'email-templates', 'closeio-crm', 'attio-crm', 'meetings-integrations', 'proposal-branding']
    : ['email-templates', 'closeio-crm', 'attio-crm', 'meetings-integrations', 'proposal-branding'];

  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tab state
  // ?tab= opens a tab on load, for example after the Attio OAuth redirect
  const [activeTab, setActiveTab] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    return tabKeys.includes(requested) ? requested : (isSuperAdmin ? 'members' : 'email-templates');
  });

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', domains: '' });
  const [saving, setSaving] = useState(false);

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ subject: '', html_content: '' });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Add member state
  const [showAddMember, setShowAddMember] = useState(false);
  const [addingMember, setAddingMember] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Members endpoints are super-admin-only on the backend; org admins reaching
      // this page must skip that call entirely rather than have it 403 the whole load.
      const [orgData, memberData, templateData] = await Promise.all([
        organizationAPI.getById(orgId),
        isSuperAdmin ? organizationAPI.getMembers(orgId) : Promise.resolve([]),
        organizationAPI.getEmailTemplates(orgId).catch(() => []),
      ]);
      setOrg(orgData);
      setMembers(memberData || []);
      setEmailTemplates(templateData || []);
      setEditForm({ name: orgData.name, description: orgData.description || '', domains: domainsToText(orgData.domains) });
    } catch (err) {
      setError(err.message || 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  }, [orgId, isSuperAdmin]);

  const handleEditTemplate = (tmpl) => {
    setEditingTemplate(tmpl);
    setTemplateForm({ subject: tmpl.subject, html_content: tmpl.html_content });
    setShowPreview(false);
  };

  const handleSaveTemplate = async () => {
    setSavingTemplate(true);
    try {
      const updated = await organizationAPI.updateEmailTemplate(orgId, editingTemplate.template_type, templateForm);
      setEmailTemplates((prev) => prev.map((t) => (t.template_type === updated.template_type ? updated : t)));
      setEditingTemplate(null);
      toast.success('Template saved');
    } catch {
      toast.error('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleResetTemplate = async () => {
    setSavingTemplate(true);
    try {
      const updated = await organizationAPI.resetEmailTemplate(orgId, editingTemplate.template_type);
      setEmailTemplates((prev) => prev.map((t) => (t.template_type === updated.template_type ? updated : t)));
      setTemplateForm({ subject: updated.subject, html_content: updated.html_content });
      toast.success('Template reset to default');
    } catch {
      toast.error('Failed to reset template');
    } finally {
      setSavingTemplate(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Domains are a super-admin-only field (backend 403s org admins), so only
      // include them in the payload for super admins.
      const payload = { name: editForm.name, description: editForm.description };
      if (isSuperAdmin) payload.domains = parseDomains(editForm.domains);
      const updated = await organizationAPI.update(orgId, payload);
      // Merge, don't replace: the update response omits logo_url/favicon_url/
      // settings/member_count, so replacing would blank those in the UI until reload.
      setOrg((prev) => ({ ...prev, ...updated }));
      setEditing(false);
      toast.success('Organization updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const updated = await organizationAPI.uploadLogo(orgId, file);
      setOrg((prev) => ({ ...prev, logo_url: updated.logo_url }));
      toast.success('Logo updated');
    } catch {
      toast.error('Failed to upload logo');
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      // Favicon persists to settings.proposal_branding.favicon_s3_key; the
      // resolved URL comes back on the branding payload.
      const branding = await organizationAPI.uploadProposalLogo(orgId, 'favicon', file);
      setOrg((prev) => ({ ...prev, favicon_url: branding.favicon_url }));
      toast.success('Favicon updated');
    } catch {
      toast.error('Failed to upload favicon');
    }
  };

  const handleAddMember = async (formData) => {
    setAddingMember(true);
    try {
      const userData = await userAPI.createUser(formData);
      if (userData.generated_password) {
        toast.success('User created successfully. Welcome email sent.', { duration: 10000 });
      } else {
        toast.success('User created successfully');
      }
      setShowAddMember(false);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to create user');
    } finally {
      setAddingMember(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await organizationAPI.updateMemberRole(orgId, userId, { role: newRole });
      setMembers((prev) =>
        prev.map((m) => (m.user_id === userId ? { ...m, role: newRole } : m))
      );
      toast.success('Role updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await organizationAPI.removeMember(orgId, userId);
      setMembers((prev) => prev.filter((m) => m.user_id !== userId));
      toast.success('Member removed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove member');
    }
  };

  const handleEnterOrg = async () => {
    try {
      await switchOrg(orgId);
      window.location.href = '/dashboard';
    } catch {
      toast.error('Failed to enter organization');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto animate-pulse">
          <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded mb-8" />
          <Card>
            <CardBody>
              <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded" />
            </CardBody>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto">
          <Alert variant="error" title="Error">{error}</Alert>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/organizations')}
          className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-primary transition-colors mb-4"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Organizations
        </button>

        {/* Org Header */}
        <Card className="mb-6">
          <CardBody>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="flex flex-col items-center gap-1">
                  <label title="Logo — shown on sign-in and in the expanded sidebar. Click to change." className="cursor-pointer group relative block w-16 h-16">
                    {org.logo_url ? (
                      <img src={org.logo_url} alt={org.name} className="w-16 h-16 rounded-lg object-contain" />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-2xl">
                        {org.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                  </label>
                  <span className="text-[10px] leading-none text-gray-400 dark:text-gray-500">Logo</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <label title="Favicon — square icon for the browser tab and collapsed sidebar. Square PNG, 256×256 recommended (min 64×64). Click to change." className="cursor-pointer group relative block w-16 h-16">
                    <div className="w-16 h-16 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 flex items-center justify-center overflow-hidden">
                      {org.favicon_url ? (
                        <img src={org.favicon_url} alt={`${org.name} favicon`} className="w-full h-full object-contain p-1" />
                      ) : (
                        <svg className="w-5 h-5 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      )}
                    </div>
                    <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <input type="file" accept="image/*" onChange={handleFaviconUpload} className="hidden" />
                  </label>
                  <span className="text-[10px] leading-none text-gray-400 dark:text-gray-500">Favicon</span>
                </div>
                <div className="flex-1">
                  {editing ? (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <Input
                          value={editForm.name}
                          onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                          className="!py-1"
                        />
                        <Button size="sm" onClick={handleSave} disabled={saving}>
                          {saving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>
                          Cancel
                        </Button>
                      </div>
                      <Textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                        rows={3}
                        maxLength={2000}
                        placeholder="Short profile or blurb about the organization (optional)"
                        className="w-full"
                        helperText={`${editForm.description.length}/2000 characters`}
                      />
                      {isSuperAdmin && (
                        <Textarea
                          label="Domains"
                          value={editForm.domains}
                          onChange={(e) => setEditForm((f) => ({ ...f, domains: e.target.value }))}
                          rows={3}
                          placeholder={"portal.example.com\napp.example.com"}
                          className="w-full"
                          helperText="One hostname per line. The login page on these domains shows this org's brand. A hostname can belong to only one org."
                        />
                      )}
                    </div>
                  ) : (
                    <>
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{org.name}</h1>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{org.slug}</p>
                      {org.description && (
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                          {org.description}
                        </p>
                      )}
                      {org.domains && org.domains.length > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          <span className="text-xs text-gray-400 dark:text-gray-500">Domains:</span>
                          {org.domains.map((d) => (
                            <Badge key={d} variant="default">{d}</Badge>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!editing && (
                  <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
                    Edit
                  </Button>
                )}
                {isSuperAdmin && (
                  <Button size="sm" onClick={handleEnterOrg}>
                    Enter Org
                  </Button>
                )}
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <Badge variant={org.is_active ? 'success' : 'default'}>
                {org.is_active ? 'Active' : 'Inactive'}
              </Badge>
              {isSuperAdmin && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {members.length} member{members.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 border-b border-gray-200 dark:border-gray-700">
          {tabKeys.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
                activeTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {tab === 'email-templates' ? 'Email Templates' : tab === 'closeio-crm' ? 'Close CRM' : tab === 'attio-crm' ? 'Attio CRM' : tab === 'meetings-integrations' ? 'Meetings & Integrations' : tab === 'proposal-branding' ? 'Proposal Branding' : 'Members'}
            </button>
          ))}
        </div>

        {/* Members Tab */}
        {isSuperAdmin && activeTab === 'members' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Members</h2>
                <Button size="sm" onClick={() => setShowAddMember(true)}>
                  Add Member
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              {members.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-4">No members</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Name</th>
                        <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Email</th>
                        <th className="text-left py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Role</th>
                        <th className="text-right py-2 px-2 text-gray-500 dark:text-gray-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((member) => (
                        <tr key={member.user_id} className="border-b border-gray-100 dark:border-gray-700/50">
                          <td className="py-2.5 px-2 text-gray-900 dark:text-white font-medium">
                            {member.first_name} {member.last_name}
                          </td>
                          <td className="py-2.5 px-2 text-gray-600 dark:text-gray-300">{member.email}</td>
                          <td className="py-2.5 px-2">
                            <select
                              value={member.role}
                              onChange={(e) => handleRoleChange(member.user_id, e.target.value)}
                              className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            >
                              {ROLE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="py-2.5 px-2 text-right">
                            <button
                              onClick={() => handleRemoveMember(member.user_id)}
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        )}

        {/* Email Templates Tab */}
        {activeTab === 'email-templates' && (
          <div className="space-y-4">
            {emailTemplates.length === 0 ? (
              <Card>
                <CardBody>
                  <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                    No email templates found. Run the database migration to seed defaults.
                  </p>
                </CardBody>
              </Card>
            ) : (
              emailTemplates.map((tmpl) => (
                <Card key={tmpl.template_type}>
                  <CardBody>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white capitalize">
                          {tmpl.template_type.replace(/_/g, ' ')} Email
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                          Subject: {tmpl.subject}
                        </p>
                        {tmpl.updated_at && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            Last updated: {new Date(tmpl.updated_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <Button size="sm" variant="secondary" onClick={() => handleEditTemplate(tmpl)}>
                        Edit
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Email Template Edit Modal */}
        {editingTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-[#242628] rounded-lg shadow-strong w-full max-w-4xl max-h-[90vh] flex flex-col">
              <div className="sticky top-0 bg-white dark:bg-[#242628] border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white capitalize">
                  Edit {editingTemplate.template_type.replace(/_/g, ' ')} Email
                </h2>
                <button onClick={() => setEditingTemplate(null)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {/* Subject */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject</label>
                  <Input
                    value={templateForm.subject}
                    onChange={(e) => setTemplateForm((f) => ({ ...f, subject: e.target.value }))}
                  />
                </div>

                {/* Variable Reference */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded p-3 text-xs text-gray-600 dark:text-gray-300">
                  <span className="font-semibold">Available variables: </span>
                  {editingTemplate.template_type === 'welcome' &&
                    '{{ first_name }}, {{ last_name }}, {{ email }}, {{ generated_password }}, {{ portal_url }}, {{ support_email }}, {{ current_year }}'}
                  {editingTemplate.template_type === 'password_reset' &&
                    '{{ first_name }}, {{ last_name }}, {{ reset_url }}, {{ portal_url }}, {{ support_email }}, {{ current_year }}'}
                  {editingTemplate.template_type === 'password_changed' &&
                    '{{ first_name }}, {{ last_name }}, {{ new_password }}, {{ changed_by_admin }}, {{ portal_url }}, {{ support_email }}, {{ current_year }}'}
                </div>

                {/* HTML Editor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">HTML Content</label>
                  <HtmlEmailEditor
                    value={templateForm.html_content}
                    onChange={(val) => setTemplateForm((f) => ({ ...f, html_content: val || '' }))}
                  />
                </div>

                {/* Preview */}
                <div>
                  <button
                    type="button"
                    onClick={() => setShowPreview((p) => !p)}
                    className="text-sm text-primary hover:underline"
                  >
                    {showPreview ? 'Hide Preview' : 'Show Preview'}
                  </button>
                  {showPreview && (
                    <iframe
                      title="Email Preview"
                      srcDoc={templateForm.html_content}
                      className="w-full mt-2 border border-gray-200 dark:border-gray-700 rounded"
                      style={{ height: 400 }}
                      sandbox="allow-same-origin"
                    />
                  )}
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
                <Button variant="secondary" size="sm" onClick={handleResetTemplate} disabled={savingTemplate}>
                  Reset to Default
                </Button>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => setEditingTemplate(null)}>Cancel</Button>
                  <Button onClick={handleSaveTemplate} disabled={savingTemplate}>
                    {savingTemplate ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Close.io CRM Tab */}
        {activeTab === 'closeio-crm' && (
          <CloseIOConfigTab orgId={orgId} org={org} />
        )}

        {/* Attio CRM Tab — independent of the Close tab; each has its own enable switch */}
        {activeTab === 'attio-crm' && (
          <AttioConfigTab orgId={orgId} />
        )}

        {/* Meetings & Integrations Tab (tl;dv / Recall / Google Meet) */}
        {activeTab === 'meetings-integrations' && (
          <MeetingsIntegrationsTab orgId={orgId} org={org} />
        )}

        {activeTab === 'proposal-branding' && (
          <ProposalBrandingTab orgId={orgId} isAdmin={true} />
        )}

        {/* Add User Modal */}
        {showAddMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-strong max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-2xl font-sans font-semibold text-gray-900">Create New User</h2>
                <button
                  onClick={() => setShowAddMember(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6">
                <UserForm
                  user={null}
                  onSubmit={handleAddMember}
                  onCancel={() => setShowAddMember(false)}
                  loading={addingMember}
                  organizations={[org]}
                  defaultOrgId={orgId}
                  isSuperAdmin={isSuperAdmin}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default OrganizationDetailPage;
